import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit } from '@angular/core';
import {
    NavigationCancel,
    NavigationEnd,
    NavigationError,
    NavigationStart,
    Router,
    RouterEvent,
    RouterOutlet,
} from '@angular/router';
import {
    ContentFilterPipe,
    ContentModelVm,
    ContentService,
    DynatraceService,
    getContentByPurpose,
    GoogleTagManagerService,
    LoadingComponent,
    LoadingService,
    OneTrustService,
    SafeMarkupPipe,
} from '@bfy/bfy-ui-shared';
import { Subscription } from 'rxjs';

@Component({
    selector: 'br-root',
    templateUrl: './app.component.html',
    styleUrl: './app.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [ContentFilterPipe, SafeMarkupPipe, RouterOutlet, LoadingComponent],
})
export class AppComponent implements OnInit, OnDestroy {
    pageContent: ContentModelVm[] = [];
    globalContent: ContentModelVm[] = [];
    subHeaderMessage!: string;
    headerTextExample!: string;
    title!: string;
    isLoading = true;

    private routerEventsSubscription!: Subscription;

    private loadingService = inject(LoadingService);
    private contentService = inject(ContentService);
    private router = inject(Router);
    private googleTagManagerService = inject(GoogleTagManagerService);
    private oneTrustService = inject(OneTrustService);
    private dynatraceService = inject(DynatraceService);

    ngOnInit(): void {
        this.oneTrustService.addToDom().then(() => {
            this.dynatraceService.addToDom();
            this.googleTagManagerService.addGtmToDom();
        });

        this.routerEventsSubscription = this.router.events.subscribe((event: any): void => {
            this.navigationInterceptor(event);
        });

        this.contentService.globalContent // XXX might need override page context option
            .subscribe({
                next: (result) => {
                    this.globalContent = result;
                    const spinnerContent: [number, string][] = [
                        [0, ''],
                        [3, getContentByPurpose(this.globalContent, 'spinnerMessageAfter3Seconds')],
                        [8, getContentByPurpose(this.globalContent, 'spinnerMessageAfter8Seconds')],
                        [
                            13,
                            getContentByPurpose(this.globalContent, 'spinnerMessageAfter13Seconds'),
                        ],
                    ];

                    this.loadingService.messages = spinnerContent;
                },
                error: (err: unknown) => console.log(err),
            });
    }

    ngOnDestroy(): void {
        this.routerEventsSubscription.unsubscribe();
    }

    get hasInformationalMessage(): boolean {
        return getContentByPurpose(this.globalContent, 'informationalMessage')?.length > 0;
    }

    private navigationInterceptor(event: RouterEvent): void {
        if (event instanceof NavigationStart) {
            this.loadingService.start();
        } else if (event instanceof NavigationEnd) {
            // undoing the spinner needs to be very slightly delayed to allow the sub-components
            // time to turn the spinner on for themselves and avoid the content "hidden" flag
            // getting set to false too soon
            setTimeout(() => this.loadingService.stop());
        } else if (event instanceof NavigationCancel) {
            this.isLoading = false;
            this.loadingService.stop();
        } else if (event instanceof NavigationError) {
            this.isLoading = false;
            this.loadingService.stop();
        }
    }
}
